/**
 * Shown while a create-project archive upload is still sending bytes.
 * This is not ingestion progress.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  dismissFailedArchiveUploads,
  retryFailedArchiveUploads,
  type ArchiveUploadSnapshot,
} from '../../../lib/archiveUploadProgress';
import { uploadProjectArchive } from '../../../services/api';
import { AlertTriangle, ArrowLeft, FileArchive, Folder, RotateCw, Upload } from 'lucide-react';
import './ingestion-aurora.css';
import './archive-upload-aurora.css';
import { EnsMark } from '../../common/EnsLoader';

function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const ArchiveUploadScreen: React.FC<{
  projectName?: string;
  upload: ArchiveUploadSnapshot;
}> = ({ projectName, upload }) => {
  const navigate = useNavigate();
  const failed = upload.status === 'failed';
  const ratio = upload.total > 0 ? Math.min(1, upload.loaded / upload.total) : 0;
  const percent = Math.round(ratio * 100);

  return (
    <div className="ingestion-aurora-page">
      <section className={`upl${failed ? ' failed' : ''}`} aria-labelledby="upl-title" aria-live="polite">
        <div className="upl-brand">
          <span className="upl-tile" aria-hidden="true">
            {failed ? <AlertTriangle size={22} /> : <Upload size={22} />}
          </span>
          <div>
            <div className="upl-eyebrow">{failed ? 'Upload failed' : 'EnsoMirai++'}</div>
            <h1 id="upl-title" className="upl-title">
              {failed ? 'Archive did not upload' : 'Uploading workspace'}
            </h1>
          </div>
        </div>

        <div className="upl-rows">
          {projectName ? (
            <div className="upl-row">
              <span className="upl-ic" aria-hidden="true">
                <Folder size={15} />
              </span>
              <span className="upl-k">Project</span>
              <span className="upl-v">{projectName}</span>
            </div>
          ) : null}
          <div className="upl-row">
            <span className="upl-ic" aria-hidden="true">
              <FileArchive size={15} />
            </span>
            <span className="upl-k">Archive</span>
            <span className="upl-v mono">
              {upload.fileName}
              {upload.fileCount && upload.fileCount > 1
                ? ` (archive ${upload.fileIndex} of ${upload.fileCount}; each becomes its own repository)`
                : ''}
            </span>
          </div>
          {failed ? (
            <div className="upl-row">
              <span className="upl-ic" aria-hidden="true">
                <AlertTriangle size={15} />
              </span>
              <span className="upl-k">Error</span>
              <span className="upl-v err">
                {upload.error || 'The archive upload stopped before ingestion could start.'}
              </span>
            </div>
          ) : null}
        </div>

        <div className="upl-progress">
          <div className="upl-top">
            <div className="upl-size">
              {formatMegabytes(upload.loaded)} <span className="of">/ {formatMegabytes(upload.total)}</span>
            </div>
            <div className="upl-pct">{percent}%</div>
          </div>
          <div
            className={`upl-bar${failed ? '' : ' active'}`}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label="Archive upload"
          >
            <span style={{ width: `${percent}%` }} />
          </div>
          <div className="upl-status" role="status">
            {failed ? null : <EnsMark size="sm" icon={14} />}
            <span>{failed ? 'Upload failed' : 'Uploading archive…'}</span>
          </div>
        </div>

        <div className="upl-actions">
          {failed && (upload.failedArchives?.length ?? 0) > 0 ? (
            <>
              <button
                type="button"
                className="upl-btn"
                onClick={() => void retryFailedArchiveUploads(upload.projectId, uploadProjectArchive).catch(() => {})}
              >
                <RotateCw size={17} aria-hidden="true" />
                Retry {upload.failedArchives!.length === 1 ? 'the failed archive' : 'the failed archives'}
              </button>
              <button type="button" className="upl-btn" onClick={() => dismissFailedArchiveUploads(upload.projectId)}>
                Continue without {upload.failedArchives!.length === 1 ? 'it' : 'them'}
              </button>
            </>
          ) : null}
          <button type="button" className="upl-btn" onClick={() => navigate('/projects/all')}>
            <ArrowLeft size={17} aria-hidden="true" />
            Back to projects
          </button>
        </div>
      </section>
    </div>
  );
};

export default ArchiveUploadScreen;
